import { Children, Component, Fragment, JSX, PropsWithChildren, ReactElement, ReactNode, isValidElement } from "react";
import React from "react";
import { BaseComponent } from "../../Core/BaseComponent/BaseComponent";
import { Guid } from "@iconics/core";
import { LayoutContent } from "./LayoutContent";
import { LayoutFooter } from "./LayoutFooter";
import { LayoutRight } from "./LayoutRight";
import { GridContainer } from "../Grid/GridContainer";
import { GridItem } from "../Grid/GridItem";

export class LayoutContainer extends BaseComponent<ILayoutContainerProps, ILayoutContainerState>
{
    public static layoutContentId = Guid.new().toString();

    private get children(): IChildren
    {
        const children = Children.toArray(this.props.children).filter(isValidElement) as ReactElement[];
        const layoutContents = children.filter(i => i.type == LayoutContent);
        const layoutFooters = children.filter(i => i.type == LayoutFooter);
        const layoutRights = children.filter(i => i.type == LayoutRight);
        const others = children.filter(i => i.type != LayoutContent && i.type != LayoutFooter && i.type != LayoutRight);

        if (layoutContents.length != 1 || layoutFooters.length > 1 || layoutRights.length > 1 || others.length > 0)
        {
            throw new Error('Expected only one LayoutContent, an optional LayoutFooter and an optional LayoutRight.');
        }

        return {
            layoutContent: layoutContents[0],
            layoutFooter: layoutFooters[0],
            layoutRight: layoutRights[0],
        };
    }

    public render(): JSX.Element
    {
        const { layoutContent, layoutFooter, layoutRight } = this.children;

        return (
            <GridContainer
                spacing="none"
                style={{ height: '100%' }}
            >
                <GridItem
                    compact={layoutRight ? 10 : 12}
                    style={{ height: '100%' }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                            minHeight: 0,
                        }}
                    >
                        <div
                            style={{
                                padding: 'var(--spacing-sd-system-dimension-spacing-extra-large)',
                                flex: '1 1 auto',
                                minHeight: 0,
                                overflowY: 'auto',
                            }}
                            onScroll={e => this.components.handleLayoutContainerScroll(e)}
                            id={LayoutContainer.layoutContentId}
                        >
                            {layoutContent}
                        </div>
                        {
                            !!layoutFooter &&
                            <div
                                style={{
                                    flex: '0 0 auto',
                                    padding: 'var(--spacing-sd-system-dimension-spacing-extra-small)',
                                }}
                            >
                                {layoutFooter}
                            </div>
                        }
                    </div>
                </GridItem>
                {
                    !!layoutRight &&
                    <GridItem
                        compact={2}
                        style={{
                            padding: 'var(--spacing-sd-system-dimension-spacing-extra-large) var(--spacing-sd-system-dimension-spacing-medium)',
                            borderLeft: 'solid var(--colors-sd-system-color-component-outline) var(--border-widths-sd-system-dimension-border-medium)',
                        }}
                    >
                        {layoutRight}
                    </GridItem>
                }
            </GridContainer>
        );
    }
}

export interface ILayoutContainerProps
{
    children?: React.ReactNode;
}

interface IChildren
{
    layoutContent: React.ReactElement;
    layoutFooter?: React.ReactElement;
    layoutRight?: React.ReactElement;
}

export interface ILayoutContainerState { }
